import React, { useEffect, useRef, useState } from "react"
import { Button, Col, Flex, Form, Input, InputNumber, Modal, Row, Radio as AntdRadio } from "antd"
import { InfoCircleOutlined, PlusOutlined } from "@ant-design/icons"
import { useMediaQuery } from "react-responsive"
import { Trans, useTranslation } from "react-i18next"
import { Card } from "ui/Card"
import { NewConfirmModal } from "ui/NewConfirmModal"
import WithEmptyStateWrapper from "ui/WithEmptyStateWrapper"
import { TicketTypeRow } from "../components/ticketType/TicketTypeRow"
import { TicketTypeCard } from "../components/ticketType/TicketTypeCard"
import { CreateTicketTypeModal } from "../components/ticketType/CreateTicketTypeModal/CreateTicketTypeModal"
import styles from "./TicketTypeStep.module.css"
import { Text } from "ui/Typography"
import { Radio } from "ui/Radio"
import { useWizardContext } from "../WizardContext"
import { buildTicketTypePayload, isServerId } from "../mappers/toApiPayload"
import { getAdjustmentsUsingTicketType } from "../helpers/ticketTypeAdjustments"
import { getBookedSlots, getTicketTypeTicketsTaken } from "../helpers/bookedTickets"

const { TextArea } = Input

export const TicketTypeStep = () => {
  const { t, i18n } = useTranslation()
  const { stepValidateHandlersRef, stepSaveHandlersRef, form, isFormInitialized, trip } = useWizardContext()
  const isWideScreen = useMediaQuery({ query: "(min-width: 1281px)" })
  const [ticketTypes, setTicketTypes] = useState([])
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingTicket, setEditingTicket] = useState(null)
  const [pendingDurationType, setPendingDurationType] = useState(null)
  // { id, usedBy } — a ticket type the guide is deleting while price adjustments use it
  const [pendingDelete, setPendingDelete] = useState(null)
  const hasLocalInit = useRef(false)
  const ticketDuration = Form.useWatch("duration_type", form)
  const isMultiDay = ticketDuration === "multi_day"

  // isFormInitialized becomes true only after WizardProvider has called form.setFieldsValue().
  // By the time this effect fires, the form already has the API data — no effect ordering risk.
  useEffect(() => {
    if (!isFormInitialized || hasLocalInit.current) return
    hasLocalInit.current = true
    const fromForm = form.getFieldValue("ticket_types") || []
    setTicketTypes(fromForm)
    // No need to write back to form — it already has this value from WizardProvider.
  }, [isFormInitialized, form])

  useEffect(() => {
    const handlers = stepValidateHandlersRef.current
    handlers["ticket_type"] = async () => {
      if (ticketTypes.length === 0) {
        const err = new Error("ticket_types_required")
        err.errorFields = [{ errors: [t("experiences.ticket_type.at_least_one_required")] }]
        throw err
      }
    }
    return () => {
      delete handlers["ticket_type"]
    }
  }, [ticketTypes, stepValidateHandlersRef, t])

  useEffect(() => {
    stepSaveHandlersRef.current["ticket_type"] = async () => {
      return buildTicketTypePayload({ ...form.getFieldsValue(), ticket_types: ticketTypes, cleared_slot_ids: form.getFieldValue("cleared_slot_ids") })
    }
    return () => {
      delete stepSaveHandlersRef.current["ticket_type"]
    }
  }, [stepSaveHandlersRef, form, ticketTypes])

  // Helpers that update both local state and form together — form stays in sync for the save handler.
  const updateTicketTypes = (next) => {
    setTicketTypes(next)
    form.setFieldValue("ticket_types", next)
  }

  const handleOpenCreate = () => {
    setEditingTicket(null)
    setIsModalOpen(true)
  }

  const handleEdit = (ticket) => {
    setEditingTicket(ticket)
    setIsModalOpen(true)
  }

  const handleSave = ({ title, description, maxCapacity }) => {
    if (editingTicket) {
      updateTicketTypes(ticketTypes.map((t) => (t.id === editingTicket.id ? { ...t, title, description, maxCapacity } : t)))
    } else {
      updateTicketTypes([...ticketTypes, { id: Date.now(), title, description, maxCapacity, enabled: true }])
    }
  }

  const handleModalClose = () => {
    setEditingTicket(null)
    setIsModalOpen(false)
  }

  // Same invariant as Base Prices: at least one ticket type must stay enabled,
  // since prices are set for enabled types on the Pricing step.
  const enabledCount = ticketTypes.filter((t) => !t._destroy && t.enabled !== false).length

  const handleToggle = (id, checked) => {
    if (!checked && enabledCount <= 1) return
    updateTicketTypes(ticketTypes.map((t) => (t.id === id ? { ...t, enabled: checked } : t)))
    // Mirror into the Base Prices state so the Pricing tab toggle matches this one
    const prices = form.getFieldValue("ticketTypePrices") || {}
    form.setFieldValue("ticketTypePrices", { ...prices, [id]: { ...prices[id], enabled: checked } })
  }

  // Start times are generated for the current duration shape (single-day slots carry an
  // end time, multi-day slots span number_of_days), so switching single↔multi invalidates
  // the schedule. Intercept the change and ask before clearing. Changing number_of_days
  // within multi-day does NOT clear the schedule.
  const handleDurationTypeChange = (event) => {
    const nextType = event.target.value
    const slots = form.getFieldValue("time_slots") || []
    const hasSlots = slots.some((slot) => !slot._destroy)
    if (!hasSlots) return
    // Form.Item has already stored the new value — restore the old one until confirmed
    form.setFieldValue("duration_type", ticketDuration ?? "single_day")
    // Clearing start times that bookings sit on would leave those bookings on deleted start times.
    const bookedCount = getBookedSlots(slots).length
    if (bookedCount > 0) {
      Modal.warning({
        title: t("experiences.ticket_type.duration_change_blocked.title"),
        content: t("experiences.ticket_type.duration_change_blocked.message", { count: bookedCount }),
        okText: t("global.ok"),
        centered: true
      })
      return
    }
    setPendingDurationType(nextType)
  }

  const handleDurationChangeConfirm = () => {
    form.setFieldValue("duration_type", pendingDurationType)
    // The saved start times are deleted by this step's own save (buildTicketTypePayload), so a
    // Save Update that goes straight back to the list doesn't leave them, or the old duration, behind.
    const clearedSlotIds = (form.getFieldValue("time_slots") || []).map((slot) => slot.id).filter(isServerId)
    form.setFieldValue("cleared_slot_ids", [...(form.getFieldValue("cleared_slot_ids") || []), ...clearedSlotIds])
    form.setFieldValue("time_slots", [])
    // Redux still holds the old slots until the scheduling step saves — this flag stops
    // TicketedSchedule's stale-cache fallback from re-seeding the slots we just cleared.
    form.setFieldValue("is_schedule_cleared", true)
    setPendingDurationType(null)
  }

  const hasTicketTypes = ticketTypes.some((ticket) => !ticket._destroy)
  const pendingDeleteTitle = ticketTypes.find((ticket) => ticket.id === pendingDelete?.id)?.title

  const scheduledSlotCount = (form.getFieldValue("time_slots") || []).filter((slot) => !slot._destroy).length

  // A saved ticket type: ask first, naming the price adjustments set to it (the server takes it out of
  // them, and deletes one left with none, when the deletion is saved; Pricing then reloads them). One
  // with tickets booked can't be deleted at all (the API refuses it), so say to switch it off instead.
  const handleDelete = (id) => {
    if (!isServerId(id)) {
      deleteTicketType(id)
      return
    }
    const taken = getTicketTypeTicketsTaken(id, form.getFieldValue("time_slots") || [])
    if (taken > 0) {
      const name = ticketTypes.find((ticket) => ticket.id === id)?.title
      Modal.warning({
        title: t("experiences.ticket_type.delete_booked_blocked.title", { name }),
        content: (
          <Trans
            i18nKey="experiences.ticket_type.delete_booked_blocked.message"
            t={t}
            i18n={i18n}
            count={taken}
            values={{ name }}
            components={{ bold: <strong /> }}
          />
        ),
        okText: t("global.ok"),
        centered: true
      })
      return
    }
    setPendingDelete({ id, usedBy: getAdjustmentsUsingTicketType(id, { form, trip }) })
  }

  const handleDeleteInUseConfirm = () => {
    deleteTicketType(pendingDelete.id)
    setPendingDelete(null)
  }

  const deleteTicketType = (id) => {
    // Temp IDs (Date.now()) → drop entirely; server IDs → mark _destroy so Rails deletes them
    if (isServerId(id)) {
      updateTicketTypes(ticketTypes.map((t) => (t.id === id ? { ...t, _destroy: true } : t)))
    } else {
      updateTicketTypes(ticketTypes.filter((t) => t.id !== id))
    }
    // Drop the orphaned base price entry so the pricing payload doesn't reference a deleted type
    // Spreading undefined is safe — yields an empty object
    const remainingPrices = { ...form.getFieldValue("ticketTypePrices") }
    delete remainingPrices[id]
    form.setFieldValue("ticketTypePrices", remainingPrices)
  }

  return (
    <div>
      <Card title={t("experiences.ticket_type.ticket_information")} className="mb-xl pb-m">
        <Flex vertical gap={16} className="border-b mb-m">
          <Text strong>{t("experiences.ticket_type.duration_label")}</Text>
          <Form.Item name="duration_type" initialValue="single_day" noStyle>
            <AntdRadio.Group className="mb-m" onChange={handleDurationTypeChange}>
              <Radio value="single_day">{t("experiences.ticket_type.single_day")}</Radio>
              <Radio value="multi_day">{t("experiences.ticket_type.multi_day")}</Radio>
            </AntdRadio.Group>
          </Form.Item>
          {isMultiDay && (
            <Flex align="center" gap={16} className="mb-m">
              <Text strong>{t("experiences.ticket_type.number_of_days_label")}</Text>
              <Form.Item
                name="number_of_days"
                rules={[{ required: true, message: t("experiences.ticket_type.number_of_days_required") }, { type: "number", min: 2, message: t("experiences.ticket_type.min_days_error") }]}
                style={{ marginBottom: 0 }}
              >
                <InputNumber min={2} precision={0} parser={(val) => val?.replaceAll(/\D/g, "")} style={{ width: 174 }} size="large" />
              </Form.Item>
            </Flex>
          )}
        </Flex>

        <Flex vertical gap={8} className="mb-m">
          <Text strong>{t("experiences.ticket_type.number_of_tickets")}</Text>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="min_tickets"
                label={t("experiences.ticket_type.min_label")}
                colon={false}
                style={{ marginBottom: 0 }}
                rules={[
                  { required: true, message: t("experiences.ticket_type.min_tickets_required") },
                  { type: "number", min: 1, message: t("experiences.ticket_type.min_tickets_greater_than_zero") }
                ]}
              >
                {/* No min prop — antd would clamp 0 to the min on blur; the rule above shows an error instead */}
                <InputNumber placeholder={t("experiences.ticket_type.min_placeholder")} min={0} precision={0} parser={(val) => val?.replaceAll(/\D/g, "")} style={{ width: "100%" }} size="large" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="max_tickets"
                label={t("experiences.ticket_type.max_label")}
                colon={false}
                style={{ marginBottom: 0 }}
                rules={[
                  { required: true, message: t("experiences.ticket_type.max_tickets_required") },
                  { type: "number", min: 1, message: t("experiences.ticket_type.max_tickets_greater_than_zero") },
                  {
                    validator: (_, value) => {
                      const min = form.getFieldValue("min_tickets")
                      if (value != null && min != null && value < min) {
                        return Promise.reject(t("experiences.ticket_type.max_tickets_greater_than_min"))
                      }
                      return Promise.resolve()
                    }
                  }
                ]}
              >
                <InputNumber placeholder={t("experiences.ticket_type.max_placeholder")} min={0} precision={0} parser={(val) => val?.replaceAll(/\D/g, "")} style={{ width: "100%" }} size="large" />
              </Form.Item>
            </Col>
          </Row>
        </Flex>

        <Form.Item
          name="ticket_description"
          label={t("experiences.ticket_type.ticket_description_label")}
          colon={false}
          style={{ marginBottom: 0 }}
          rules={[{ required: true, message: t("experiences.ticket_type.ticket_description_required") }]}
        >
          <TextArea placeholder={t("experiences.ticket_type.description_input_placeholder")} rows={4} size="large" />
        </Form.Item>
      </Card>

      <Card
        title={t("experiences.ticket_type.ticket_types_title")}
        className="mb-xl"
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenCreate} className={styles.addNewBtn}>
            {t("experiences.ticket_type.add_new")}
          </Button>
        }
      >
        <WithEmptyStateWrapper
          name="ticket type"
          hasItems={hasTicketTypes}
          fallback={
            <Flex justify="center" className={styles.emptyState}>
              <Text className={styles.emptyText}>{t("experiences.ticket_type.empty_state")}</Text>
            </Flex>
          }
        >
          <Flex vertical gap={8}>
            {ticketTypes.filter((t) => !t._destroy).map((ticket, _, visible) => {
              const isLastEnabled = ticket.enabled !== false && enabledCount <= 1
              return isWideScreen ? (
                <TicketTypeRow key={ticket.id} ticket={ticket} isLast={visible.length === 1} isLastEnabled={isLastEnabled} onToggle={handleToggle} onEdit={handleEdit} onDelete={handleDelete} />
              ) : (
                <TicketTypeCard key={ticket.id} ticket={ticket} isLast={visible.length === 1} isLastEnabled={isLastEnabled} onToggle={handleToggle} onEdit={handleEdit} onDelete={handleDelete} />
              )
            })}
          </Flex>
        </WithEmptyStateWrapper>
        {hasTicketTypes && (
          <Flex align="center" gap={6} className={styles.priceInfo}>
            <InfoCircleOutlined className={styles.infoIcon} />
            <Text className={styles.priceInfoText}>{t("experiences.ticket_type.prices_next_step")}</Text>
          </Flex>
        )}
      </Card>

      <CreateTicketTypeModal
        open={isModalOpen}
        onCancel={handleModalClose}
        onSave={handleSave}
        initialValues={editingTicket ? { title: editingTicket.title, description: editingTicket.description, maxCapacity: editingTicket.maxCapacity } : null}
      />

      <NewConfirmModal
        visible={pendingDurationType !== null}
        onClose={() => setPendingDurationType(null)}
        onConfirm={handleDurationChangeConfirm}
        title={t("experiences.ticket_type.duration_change_confirm.title")}
        message={
          <Trans
            i18nKey="experiences.ticket_type.duration_change_confirm.message"
            values={{ count: scheduledSlotCount }}
            components={{ bold: <strong /> }}
          />
        }
        confirmLabel={t("global.confirm")}
      />

      <NewConfirmModal
        visible={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        onConfirm={handleDeleteInUseConfirm}
        title={t("experiences.ticket_type.delete_in_use_confirm.title", { name: pendingDeleteTitle })}
        message={
          <Trans
            i18nKey={
              pendingDelete?.usedBy.length
                ? "experiences.ticket_type.delete_in_use_confirm.message"
                : "experiences.ticket_type.delete_confirm.message"
            }
            count={pendingDelete?.usedBy.length ?? 0}
            values={{ name: pendingDeleteTitle, adjustments: (pendingDelete?.usedBy || []).join(", ") }}
            components={{ bold: <strong /> }}
          />
        }
        confirmLabel={t("global.delete")}
      />
    </div>
  )
}
